/**
 * Silent, non-blocking geolocation capture.
 *
 * Used to tag a sale/order with the coordinates where it physically happened,
 * without any visible UI, loading state, or error message to the seller. If
 * geolocation is unsupported, permission is denied, or the request times out,
 * this resolves to `null` so the calling flow can proceed unaffected -- it
 * never rejects.
 *
 * Default timeout is short (2s, not the browser's usual longer default)
 * because callers `await` this before submitting a sale/order: the whole
 * point is a coordinate "if it's quick to get", not a delay the seller
 * notices on every single save, especially indoors where a GPS fix can take
 * much longer than that to land.
 */
export function getCurrentPositionSafe(
  timeoutMs = 2000
): Promise<{ latitude: number; longitude: number } | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null)
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: timeoutMs }
    )
  })
}
